type RateLimitPayload = {
  error?: string;
  message?: string;
};

type RateLimitErrorLike = {
  config?: {
    url?: string;
  };
  response?: {
    status?: number;
    data?: unknown;
  };
};

type RateLimitAlertDetail = {
  message: string;
};

export const RATE_LIMIT_STATUS = 429;
export const LOGIN_RATE_LIMIT_COOLDOWN_SECONDS = 60;
export const REGISTRATION_RATE_LIMIT_COOLDOWN_SECONDS = 60 * 60;

const RATE_LIMIT_ALERT_EVENT = 'app:rate-limit-alert';

const isRateLimitErrorLike = (error: unknown): error is RateLimitErrorLike => {
  return Boolean(error && typeof error === 'object' && 'response' in error);
};

const tryParsePayload = (payload: unknown): RateLimitPayload | null => {
  if (!payload) {
    return null;
  }

  if (typeof payload === 'string') {
    try {
      return JSON.parse(payload) as RateLimitPayload;
    } catch {
      return {
        message: payload,
      };
    }
  }

  if (typeof payload === 'object') {
    return payload as RateLimitPayload;
  }

  return null;
};

export const isRateLimitError = (error: unknown): boolean => {
  return isRateLimitErrorLike(error) && error.response?.status === RATE_LIMIT_STATUS;
};

export const getRateLimitMessage = (
  error: unknown,
  fallback = 'Too many requests. Please try again later.',
): string => {
  if (!isRateLimitErrorLike(error) || error.response?.status !== RATE_LIMIT_STATUS) {
    return fallback;
  }

  const payload = tryParsePayload(error.response?.data);
  return payload?.message || fallback;
};

export const getRateLimitRequestUrl = (error: unknown): string | undefined => {
  if (!isRateLimitErrorLike(error)) {
    return undefined;
  }

  return error.config?.url;
};

export const dispatchRateLimitAlert = (message: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(
    new CustomEvent<RateLimitAlertDetail>(RATE_LIMIT_ALERT_EVENT, {
      detail: { message },
    }),
  );
};

export const subscribeToRateLimitAlerts = (
  listener: (detail: RateLimitAlertDetail) => void,
) => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const handleAlert = (event: Event) => {
    const customEvent = event as CustomEvent<RateLimitAlertDetail>;

    if (customEvent.detail?.message) {
      listener(customEvent.detail);
    }
  };

  window.addEventListener(RATE_LIMIT_ALERT_EVENT, handleAlert);

  return () => {
    window.removeEventListener(RATE_LIMIT_ALERT_EVENT, handleAlert);
  };
};
